import { AfterViewInit, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { combineLatest, firstValueFrom, forkJoin, of } from 'rxjs';
import { finalize, first, startWith, takeUntil, switchMap } from 'rxjs/operators';
import { AgreementFormBaseComponent } from 'src/app/base-components/worker-agreement-form-base.component';
import { Permission } from 'src/app/common/enums';
import { Messages } from 'src/app/common/enums/messages';
import { CreateMultipleWorkerAgreementsRequest } from 'src/app/contracts/requests/create-multiple-worker-agreements-request';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { OrderService } from 'src/app/data/order.service';
import { SupervisorsService } from 'src/app/data/supervisors.service';
import { WorkerAgreementService } from 'src/app/data/worker-agreement.service';
import { WorkerService } from 'src/app/data/worker.service';
import { CompanyDto } from 'src/app/models/dtos/CompanyDto';
import { LegalizationDataForWorkerAgreementDto } from 'src/app/models/dtos/worker-agreements/legalization-data-for-worker-agreement-dto';
import { WorkerDataForAgreementFormDto } from 'src/app/models/dtos/workers/worker-data-for-agreement-form-dto';
import { EmploymentType } from 'src/app/models/enums/employment-type-enum';
import { WorkerAgreement } from 'src/app/models/WorkerAgreement';
import { CheckEmploymentHistoryDialogService } from 'src/app/shared/messages/check-employment-history-dialog/service/check-employment-history-dialog.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { ModulePermissionService } from 'src/app/subscription-package';
import { LegalizationService } from '../../data/legalization.service';
import { EMPTY } from 'rxjs';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-multiple-agreement-form',
  templateUrl: './multiple-agreement-form.component.html',
  styleUrls: ['./multiple-agreement-form.component.scss'],
  standalone: false
})
export class MultipleAgreementFormComponent extends AgreementFormBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  private workerService = inject(WorkerService);
  private checkEmploymentHistoryDialogService = inject(CheckEmploymentHistoryDialogService);

  protected employmentTypeId: EmploymentType;

  agreementFormGroup: UntypedFormGroup;
  selectedWorkers: any[];
  isExtension: boolean;
  selectedCompany: CompanyDto;
  foreignWorkers = new Set<number>();
  workerAgreementLegalizationData: LegalizationDataForWorkerAgreementDto[] | null = null;

  get isSubmitForLegalizationEnabled(): boolean {
    const formData = this.agreementFormGroup.getRawValue();
    return !!(formData.company && formData.employer && formData.employerObject);
  }


  constructor() {
    super();

    const matchAll = this.router.url.match(/\d+/g);
    this.employmentTypeId = +matchAll[0];
    this.selectedWorkers = this.getSelectedWorkers();
    this.isExtension = this.getExtension();
    this.workerAgreementId = this.selectedWorkers[0].AgreementId;
    this.indefiniteContractPossible = this.employmentTypeId != EmploymentType.TemporaryMandateAgreement;
    this.handleLocationChange();
    this.onSupervisorChange();
  }

  get foreignWorkersWithData(): Array<{
    id: number;
    name: string;
    legalizationData: LegalizationDataForWorkerAgreementDto;
  }> {
    if (!this.workerAgreementLegalizationData || this.foreignWorkers.size === 0) {
      return [];
    }

    return this.selectedWorkers
      .filter((worker) => this.foreignWorkers.has(worker.Id))
      .map((worker) => {
        const legalizationData = this.workerAgreementLegalizationData!.find((data) => data.WorkerId === worker.Id);
        return {
          id: worker.Id,
          name: worker.Name,
          legalizationData,
        };
      })
      .filter((item) => item.legalizationData);
  }

  async ngOnInit(): Promise<void> {
    this.onCompanyChange();

    if (this.isPermittedToLegalization()) {
      this.workerService
        .getWorkerDataForAgreementForm(this.selectedWorkers.map((w) => w.Id))
        .pipe(first())
        .subscribe(async (data: WorkerDataForAgreementFormDto[]) => {
          this.foreignWorkers = new Set(data.filter((worker) => worker.IsForeigner).map((worker) => worker.WorkerId));
          this.workerAgreementLegalizationData = data.map((dto) => ({
            WorkerId: dto.WorkerId,
            LegalizationStatusName: null,
            LegalizationStatusId: null,
          }));
        });
    }

    if (this.isExtension) {
      this.fetchWorkerAgreement(this.workerAgreementId);
    } else {
      this.addNewWage();
      await this.setInitialValues(null);
    }

    this.initEventHandlers();
    this.getCurrentSupervisor();
    this.handleResponsibilitiesChange();
  }

  ngAfterViewInit(): void {
    this.handleEmployerSelectionChange();
    this.handleIsForIndefinitePeriodChange();
    this.checkEmploymentHistoryDialogService.checkAndOpenModal(this.employmentTypeId);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  submit = () => {
    if (this.agreementFormGroup.invalid) return;

    this.spinner.show();

    this.workerAgreementService
      .createMultipleWorkerAgreements(
        this.request(
          this.selectedWorkers.map((w) => w.Id),
          this.agreementFormGroup.getRawValue(),
        ),
      )
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((_) => {
        sessionStorage.removeItem('selectedWorkers');
        this.snackbarService.openSuccessSnackBar(
          this.isExtension
            ? Messages.SuccessfullyStartedMultipleAgreementExtensionGenerationProcess
            : Messages.SuccessfullyStartedMultipleAgreementGenerationProcess,
        );
        this.router.navigate(['/workerAgreements']);
      });
  };

  protected onSubmitForLegalization(): void {
    if (!this.isSubmitForLegalizationEnabled) {
      this.markRequiredLegalizationFieldsAsInvalid();
      return;
    }

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmSubmitForMultipleLegalization } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? super.submitForMultipleLegalizations(this.selectedWorkers.map((w) => w.Id)) : EMPTY)),
      )
      .subscribe((processedWorkerIds: number[]) => {
        if (processedWorkerIds && processedWorkerIds.length > 0) {
          this.selectedWorkers = this.selectedWorkers.filter((worker) => !processedWorkerIds.includes(worker.Id));

          if (this.selectedWorkers.length > 0) {
            sessionStorage.setItem('selectedWorkers', JSON.stringify(this.selectedWorkers));
            this.foreignWorkers = new Set<number>([...this.foreignWorkers].filter((num) => !processedWorkerIds.includes(num)));
          } else {
            sessionStorage.removeItem('selectedWorkers');
            this.router.navigate(['/workerAgreements']);
          }
        }
      });
  }

  private markRequiredLegalizationFieldsAsInvalid(): void {
    const requiredFields = ['company', 'employer', 'employerObject'];

    requiredFields.forEach((fieldName) => {
      const control = this.agreementFormGroup.get(fieldName);
      if (!control.value) {
        control.setErrors({ required: true });
        control.markAsTouched();
      }
    });
  }

  private fetchWorkerAgreement(agreementId: number): void {
    if (!agreementId) {
      return;
    }

    this.workerAgreementService.getExtensionMultipleMandateAgreementBaseData(agreementId)
      .pipe(first())
      .subscribe(async (res) => {
        this.setWorkerAgreement(res);
        await this.setInitialValues(this.workerAgreement);
        const companies = await firstValueFrom(this.listOfCompanies$);
        this.selectedCompany = companies.find((c) => c.Id === this.company.value) || null;
      });
  }

  onCompanyChange(): void {
    combineLatest([this.company.valueChanges.pipe(startWith(this.company.value)), this.listOfCompanies$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([companyId, companies]) => {
        if (companyId && companies) {
          this.selectedCompany = companies.find((c) => c.Id === companyId) || null;
        }
      });
  }

  private isPermittedToLegalization(): boolean {
    return (
      this.authService.hasPermission(Permission.LegalizationInformationVisibilityToAll) ||
      this.authService.hasPermission(Permission.LegalizationInformationVisibilityToEmployerObjects)
    );
  }

  private request = (workerIds: number[], formValue: any): CreateMultipleWorkerAgreementsRequest => ({
    WorkerIds: workerIds,
    CompanyId: formValue.company,
    EmployerId: formValue.employer,
    EmployerObjectId: formValue.employerObject.Id,
    LocationId: this.selectedEmployerObject.HasLocalization ? formValue.location.Id : null,
    AgreementTypeId: formValue.agreementType,
    Responsibilities: formValue.responsibilities.map((res) => res.Id),
    ConclusionDate: formValue.conclusionDate,
    EmploymentDateFrom: formValue.employmentDateFrom,
    EmploymentDateTo: formValue.employmentDateTo,
    IsPreliminaryMedicalExaminationRequired:
      this.selectedEmployerObject.HasPreliminaryMedicalExaminationRequirements && formValue.preliminaryMedicalExamination,
    Wages: formValue.wages,
    AgreementTemplateId: formValue.agreementTemplate,
    NoticePeriodId: formValue.noticePeriod,
    PaymentDeadlineId: formValue.paymentDeadline,
    IsExtension: this.isExtension,
    PotentialSupervisorId: this.selectedEmployerObject.RequireSupervisorOnMultiAgreementsForm ? formValue.supervisor?.Id : null,
    WorkPatternId: this.selectedEmployerObject.HasWorkPattern ? formValue.workPattern : null,
    EmploymentTypeId: this.employmentTypeId,
    OrderItemId: this.orderItem.value?.Id
  });

  private getSelectedWorkers = () => {
    const workersFromHistory = history.state.workers;

    if (!!workersFromHistory?.length) {
      sessionStorage.setItem('selectedWorkers', JSON.stringify(workersFromHistory));
      return workersFromHistory;
    }

    const workersFromSessionStorage = sessionStorage.getItem('selectedWorkers');
    if (!!workersFromSessionStorage) {
      return JSON.parse(workersFromSessionStorage);
    }

    return [];
  };

  private getExtension = () => !!history.state.extension;

  isLegalizationFieldVisible(): boolean {
    return this.foreignWorkers.size > 0 && this.selectedCompany?.HasLegalization;
  }

  protected override configureSupervisor() {
    if (this.selectedEmployerObject.RequireSupervisorOnMultiAgreementsForm) {
      this.supervisor.enable();
    } else {
      this.supervisor.disable();
    }
  }
}
